import { useCallback, useState } from 'react';

/**
 * `localStorage`-backed state.
 *
 * Several small preferences live in `localStorage` - the collapsed nav rail,
 * the selected sub-section, the cached activity record. Read inline and
 * unguarded, each of those throws outright in Safari's private mode; routing
 * them through here fixes that without changing the behaviour.
 */
export function useLocalStorage<T>(key: string, initialValue: T) {
  const [value, setValue] = useState<T>(() => {
    try {
      const stored = localStorage.getItem(key);
      return stored === null ? initialValue : (JSON.parse(stored) as T);
    } catch {
      // Missing, unparseable, or storage unavailable - fall back rather than
      // taking the whole shell down on a bad cache entry.
      return initialValue;
    }
  });

  const set = useCallback(
    (next: T | ((current: T) => T)) => {
      setValue((current) => {
        const resolved = typeof next === 'function' ? (next as (c: T) => T)(current) : next;

        try {
          localStorage.setItem(key, JSON.stringify(resolved));
        } catch {
          /* value still applies for this session */
        }

        return resolved;
      });
    },
    [key]
  );

  const remove = useCallback(() => {
    try {
      localStorage.removeItem(key);
    } catch {
      /* nothing to remove */
    }
  }, [key]);

  return [value, set, remove] as const;
}

/** Plain helpers for the call sites that read storage outside React state. */
export const storage = {
  get(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      /* ignore */
    }
  },
  remove(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  },
  getJson<T>(key: string): T | null {
    const raw = storage.get(key);

    if (raw === null) {
      return null;
    }

    try {
      return JSON.parse(raw) as T;
    } catch {
      // A corrupt entry is worse than no entry - clear it so it cannot keep
      // failing on every load.
      storage.remove(key);
      return null;
    }
  },
  setJson(key: string, value: unknown): void {
    try {
      storage.set(key, JSON.stringify(value));
    } catch {
      /* ignore */
    }
  }
};
